// @flow
import * as React from 'react';
import { translate } from 'react-i18next';

import styled, { withTheme } from 'styled-components';
import TextField from '@material-ui/core/TextField';

import { ButtonSection, ButtonSubmit } from 'common/modules/authentication/styles';
import Email from '../../../../statsradio/domain/Email';
import type { FolloUserProfile } from '../domain/FolloUserProfile';
import * as i18n from '../../../../common/i18n/i18n';

const Form = styled.form`
  width: 100%;
  padding: 20px 20%;
`;

const SButtonSection = styled(ButtonSection)`
  display: flex;
  justify-content: center;
  margin: 20px;
`;

const PasswordValidation = styled.div`
  font-size: 12px;
`;

export const SUBMIT_BUTTON_ID = 'SUBMIT_BUTTON_ID';

interface Props {
  t: any;
  campaignReference: string,
  onSignUp: (string, FolloUserProfile) => void,
}

interface State {
  folloUserProfile: FolloUserProfile,
  advertiserNameError: boolean;
  firstNameError: boolean;
  surnameError: boolean;
  emailError: boolean;
  passwordError: boolean;
}

const NEW_FOLLO_USER_PROFILE = {
  advertiserName: '',
  firstName: '',
  surname: '',
  email: '',
  password: '',
  confirmPassword: '',
};

function mapEventTargetValueToCallback(callBack) {
  return e => callBack(e.target.value);
}

function validateNotEmpty(value: string, onError?: () => void): boolean {
  if (value === '') {
    if (onError) {
      onError();
    }
    return false;
  }
  return true;
}

export class SignUpForm extends React.Component<Props, State> {
  state = {
    folloUserProfile: { ...NEW_FOLLO_USER_PROFILE, language: i18n.getCurrentLanguage() },
    advertiserNameError: false,
    firstNameError: false,
    surnameError: false,
    emailError: false,
    passwordError: false,
  };

  resetEmailError = () => {
    this.setState({ emailError: false });
  };

  resetPasswordError = () => {
    this.setState({ passwordError: false });
  };

  resetFirstNameError = () => {
    this.setState({ firstNameError: false });
  };

  resetSurnameError = () => {
    this.setState({ surnameError: false });
  };

  resetAdvertiserError = () => {
    this.setState({ advertiserNameError: false });
  };

  onAdvertiserNameChange = (advertiserName: string) => {
    const { folloUserProfile } = this.state;
    this.resetAdvertiserError();
    this.setState({ folloUserProfile: { ...folloUserProfile, advertiserName: advertiserName } });
  };

  onFirstNameChange = (firstName: string) => {
    const { folloUserProfile } = this.state;
    this.resetFirstNameError();
    this.setState({ folloUserProfile: { ...folloUserProfile, firstName } });
  };

  onSurnameChange = (surname: string) => {
    const { folloUserProfile } = this.state;
    this.resetSurnameError();
    this.setState({ folloUserProfile: { ...folloUserProfile, surname } });
  };

  onEmailChange = (email: string) => {
    const { folloUserProfile } = this.state;
    this.resetEmailError();
    this.setState({ folloUserProfile: { ...folloUserProfile, email } });
  };

  onPasswordChange = (password: string) => {
    const { folloUserProfile } = this.state;
    this.resetPasswordError();
    this.setState({ folloUserProfile: { ...folloUserProfile, password } });
  };

  onConfirmPasswordChange = (confirmPassword: string) => {
    const { folloUserProfile } = this.state;
    this.resetPasswordError();
    this.setState({ folloUserProfile: { ...folloUserProfile, confirmPassword } });
  };

  signup = () => {
    this.validatePassword();
    this.validateFirstName();
    this.validateSurname();
    this.validateEmail();
    this.validateAdvertiserName();

    if (this.isValid()) {
      this.props.onSignUp(this.props.campaignReference, this.state.folloUserProfile);
    }
  };

  isValid = (): boolean => {
    return this.emailIsValid() && this.passwordIsValid() && this.advertiserNameIsValid() && this.firstNameIsValid() && this.surnameIsValid();
  };

  advertiserNameIsValid = (): boolean => {
    return validateNotEmpty(this.state.folloUserProfile.advertiserName);
  };

  validateAdvertiserName = () => {
    validateNotEmpty(this.state.folloUserProfile.advertiserName, () => this.setState({ advertiserNameError: true }));
  };

  firstNameIsValid = (): boolean => {
    return validateNotEmpty(this.state.folloUserProfile.firstName);
  };

  validateFirstName = () => {
    validateNotEmpty(this.state.folloUserProfile.firstName, () => this.setState({ firstNameError: true }));
  };

  surnameIsValid = (): boolean => {
    return validateNotEmpty(this.state.folloUserProfile.surname);
  };

  validateSurname = () => {
    validateNotEmpty(this.state.folloUserProfile.surname, () => this.setState({ surnameError: true }));
  };

  emailIsValid = (): boolean => {
    return Email.isValid(this.state.folloUserProfile.email);
  };

  validateEmail = () => {
    if (!this.emailIsValid()) {
      this.setState({ emailError: true });
    }
  };

  passwordIsValid = (): boolean => {
    const { password, confirmPassword } = this.state.folloUserProfile;
    return password !== '' && password === confirmPassword;
  };

  validatePassword = () => {
    if (!this.passwordIsValid()) {
      this.setState({ passwordError: true });
    }
  };

  render() {
    const { t } = this.props;
    const { folloUserProfile } = this.state;

    return (
      <Form>
        <TextField
          fullWidth
          id="advertiserName"
          onChange={mapEventTargetValueToCallback(this.onAdvertiserNameChange)}
          value={folloUserProfile.advertiserName}
          label={t('advertiserName')}
          margin="normal"
          helperText={this.state.advertiserNameError ? t('mustNotBeEmpty') : undefined}
          error={this.state.advertiserNameError}
        />
        <TextField
          fullWidth
          id="email"
          onChange={mapEventTargetValueToCallback(this.onEmailChange)}
          value={folloUserProfile.email}
          label={t('email')}
          margin="normal"
          type="email"
          helperText={this.state.emailError ? t('invalidEmailMessage') : undefined}
          error={this.state.emailError}
        />

        <div className={'row'}>
          <div className={'col-sm-12 col-md-6'}>
            <TextField
              fullWidth
              id="firstName"
              onChange={mapEventTargetValueToCallback(this.onFirstNameChange)}
              value={folloUserProfile.firstName}
              label={t('firstName')}
              margin="normal"
              helperText={this.state.firstNameError ? t('mustNotBeEmpty') : undefined}
              error={this.state.firstNameError}
            />
          </div>
          <div className={'col-sm-12 col-md-6'}>
            <TextField
              fullWidth
              id="surname"
              onChange={mapEventTargetValueToCallback(this.onSurnameChange)}
              value={folloUserProfile.surname}
              label={t('surname')}
              margin="normal"
              helperText={this.state.surnameError ? t('mustNotBeEmpty') : undefined}
              error={this.state.surnameError}
            />
          </div>
        </div>
        <div className={'row'}>
          <div className={'col-sm-12 col-md-6'}>
            <TextField
              fullWidth
              id="password"
              onChange={mapEventTargetValueToCallback(this.onPasswordChange)}
              value={folloUserProfile.password}
              label={t('password')}
              type="password"
              margin="normal"
              helperText={this.state.passwordError ? t('invalidPasswordMessage') : undefined}
              error={this.state.passwordError}
            />
          </div>
          <div className={'col-sm-12 col-md-6'}>
            <TextField
              fullWidth
              id="confirmPassword"
              onChange={mapEventTargetValueToCallback(this.onConfirmPasswordChange)}
              value={folloUserProfile.confirmPassword}
              label={t('confirmPassword')}
              type="password"
              margin="normal"
              helperText={this.state.passwordError ? t('invalidPasswordMessage') : undefined}
              error={this.state.passwordError}
            />
          </div>
        </div>
        <PasswordValidation>* {t('passwordValidationMessage')}</PasswordValidation>
        <SButtonSection>
          <ButtonSubmit type="button" id={SUBMIT_BUTTON_ID} onClick={this.signup}>
            {t('signup')}<i className="material-icons" style={{ verticalAlign: 'middle' }}>chevron_right</i>
          </ButtonSubmit>
        </SButtonSection>
      </Form>
    );
  }
}

export default translate('signup')(withTheme(SignUpForm));



// WEBPACK FOOTER //
// ./src/advertisers/modules/signup/components/SignUpForm.js